// src/pages/TablerosAdminPage.jsx
// Tableros: es la versión nueva (era "Tableros V2") y quedó como el tablero de
// la app para todos los roles. El anterior sigue vivo en /tableros-respaldo,
// dentro del menu de admin, como respaldo.
//
// El nombre del archivo quedó como estaba para no mover el componente con sus
// imports y el doc: si algún día se renombra, el que sigue siendo el tablero
// principal es este.
import React, { useRef, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { useObrasData } from '../hooks/useObrasData';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import TableroPDF from '../components/dashboard/TableroPDF';
import TablerosAdminFilters from '../components/dashboard/TablerosAdminFilters';
import GraficoPartidos, { construirOpcionPartidos } from '../components/dashboard/GraficoPartidos';
import { GraficoTortaEstados } from '../components/dashboard/GraficoTortaEstados';
import { ComponenteMapas, calcularRangosDinamicos } from '../components/dashboard/ComponenteMapas';
import { SelectorGraficoTipoSubsidio } from '../components/dashboard/SelectorGraficoTipoSubsidio';
import { GraficoRecursosPorTipoAnual } from '../components/dashboard/GraficoRecursosPorTipoAnual';
import { GraficoSubsidiosAnual } from '../components/dashboard/GraficoSubsidiosAnual';
import { GraficoRecursosPorTipoAnualApilado } from '../components/dashboard/GraficoRecursosPorTipoAnualApilado';
import { GraficoSubsidiosAnualApilado } from '../components/dashboard/GraficoSubsidiosAnualApilado';

export const TablerosAdminPage = () => {
	const {
		metrics,
		proyectosPorPartido,
		proyectosPorPartidoBase,
		proyectosPorPrograma,
		proyectosPorEstado,
		recursosPorSubsidioAnual,
		proyectosPorSubsidioAnual,
		loading,
		isRefetching,
		error,
		filters,
		updateFilters,
		resetFilters
	} = useObrasData({
		year: 'Todos',
		criterioYear: 'pago_anticipo',
		partido: 'Todas',
		tipo: 'Todos',
		subtipo: 'Todos',
		estado: 'Todos',
		area_responsable: 'Todos',
		mes: ''
	});

	// 📌 Refs de las instancias de cada gráfico para capturarlas como imagen
	// en el PDF (mismo criterio que el Informe técnico: el PDF se arma como
	// texto con react-pdf y cada gráfico/mapa entra como <Image>, sin convertir
	// la página entera a canvas).
	const gaugeRef = useRef(null);
	const tortaProgramasRef = useRef(null);
	const tortaEstadosRef = useRef(null);
	const recursosAnualRef = useRef(null);
	const proyectosAnualRef = useRef(null);
	const mapasRef = useRef(null);
	const [generandoPdf, setGenerandoPdf] = useState(false);
	// El toggle "Top 15 / Todos" de Proyectos por Partido lo controla la página:
	// el PDF necesita saber la vista para armar el gráfico (sin zoom, para que
	// entren todas las barras) y la tabla (todos los partidos en vista "Todos").
	const [mostrarTop15Partidos, setMostrarTop15Partidos] = useState(true);

	const capturarChart = (instancia) =>
		instancia
			? instancia.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
			: null;

	// El gráfico de Proyectos por Partido se re-renderiza FUERA de pantalla con
	// el mismo optionBuilder de la tarjeta pero sin dataZoom: en pantalla la
	// ventana de zoom es necesaria (tarjeta de alto fijo) y la captura directa
	// mostraría solo una porción; acá se crea una instancia nueva con la opción
	// completa y se captura al instante. Si la vista es "Todos" se le da más
	// altura (hasta un tope que sigue entrando en la hoja) para que las barras
	// de todos los partidos se distingan.
	const capturarPartidosPDF = () => {
		const universo = proyectosPorPartidoBase.length > 0 ? proyectosPorPartidoBase : proyectosPorPartido;
		const nBarras = mostrarTop15Partidos ? 15 : universo.length;
		const alto = mostrarTop15Partidos ? 500 : Math.min(1000, Math.max(500, nBarras * 18));

		const contenedor = document.createElement('div');
		contenedor.style.cssText = `position:absolute;left:-10000px;top:0;width:900px;height:${alto}px;`;
		document.body.appendChild(contenedor);

		let instancia = null;
		try {
			instancia = echarts.init(contenedor, null, { renderer: 'canvas' });
			instancia.setOption(construirOpcionPartidos({
				data: proyectosPorPartido,
				baseData: proyectosPorPartidoBase,
				partidoSeleccionados: filters.partido,
				mostrarTop15: mostrarTop15Partidos,
				conDataZoom: false
			}));
			return instancia.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
		} finally {
			if (instancia) instancia.dispose();
			contenedor.remove();
		}
	};

	const generarPDF = async () => {
		try {
			setGenerandoPdf(true);

			// Filtros aplicados (solo los que no están en "Todos/Todas")
			const filtrosAplicados = [];
			const agregarFiltro = (clave, valor) => {
				if (!valor || valor === 'Todos' || valor === 'Todas') return;
				filtrosAplicados.push({ clave, valor: Array.isArray(valor) ? valor.join(', ') : String(valor) });
			};
			// El criterio de año (por exp. o por pago) SIEMPRE está activo: es el
			// filtro por defecto del tablero, así que se muestra aunque el resto
			// estén en "Todos".
			const CRITERIO_LABELS = {
				expediente: 'Actual (por año exp. de proyecto)',
				pago_anticipo: 'Pago (anticipo o total)',
			};
			const criterio = filters.criterioYear || 'pago_anticipo';
			filtrosAplicados.push({ clave: 'Criterio', valor: CRITERIO_LABELS[criterio] || criterio });
			agregarFiltro('Año', filters.year);
			agregarFiltro('Partido', filters.partido);
			agregarFiltro('Tipo', filters.tipo);
			agregarFiltro('Subtipo', filters.subtipo);
			agregarFiltro('Estado', filters.estado);
			agregarFiltro('Área responsable', filters.area_responsable);
			agregarFiltro('Mes', filters.mes);

			const mapaProvincia = mapasRef.current?.capturarProvincia?.();
			const mapaConurbano = mapasRef.current?.capturarConurbano?.();

			const imagenes = {
				gauge: capturarChart(gaugeRef.current),
				tortaProgramas: capturarChart(tortaProgramasRef.current),
				partidos: capturarPartidosPDF(),
				tortaEstados: capturarChart(tortaEstadosRef.current),
				recursosAnual: capturarChart(recursosAnualRef.current),
				proyectosAnual: capturarChart(proyectosAnualRef.current),
				mapaProvincia: mapaProvincia?.src,
				mapaConurbano: mapaConurbano?.src,
			};

			const documento = pdf(
				<TableroPDF
					kpis={metrics || {}}
					filtrosAplicados={filtrosAplicados}
					imagenes={imagenes}
					// Misma función que usa el mapa en pantalla: la leyenda del PDF
					// se dibuja como vector y necesita los límites de los cuantiles.
					rangosLeyenda={calcularRangosDinamicos(proyectosPorPartidoBase)}
					mapaProvinciaRatio={mapaProvincia?.ratio}
					mapaConurbanoRatio={mapaConurbano?.ratio}
					recursosAnualData={recursosPorSubsidioAnual}
					proyectosAnualData={proyectosPorSubsidioAnual}
					partidosData={proyectosPorPartidoBase}
					partidosMostrarTop15={mostrarTop15Partidos}
					fecha={new Date().toLocaleDateString('es-AR')}
				/>
			);

			const blob = await documento.toBlob();
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'tablero_indicadores.pdf';
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error al generar el PDF del tablero:', error);
		} finally {
			setGenerandoPdf(false);
		}
	};

	// 📊 OPCIONES ECHARTS: Torta de Programas (Estilo Anillo Moderno, Tablero V1)
	const optionTortaProgramas = {
		tooltip: {
			trigger: 'item',
			formatter: '{b}: <b>{c}</b> ({d}%)'
		},
		legend: {
			type: 'scroll',
			orient: 'vertical',
			right: '5%',
			top: 'center',
			itemGap: 12,
			textStyle: {
				color: '#4b5563',
				fontSize: 12
			},
			formatter: function (name) {
				const encontrado = proyectosPorPrograma.find(
					p => (p.programa || 'Sin Programa Asignado') === name
				);
				const cantidad = encontrado ? encontrado.cantidad : 0;
				const nombreCorto = name.length > 30 ? name.substring(0, 27) + '...' : name;
				return `${nombreCorto} (${cantidad})`;
			}
		},
		series: [
			{
				name: 'Programas',
				type: 'pie',
				radius: ['40%', '70%'],
				center: ['30%', '50%'],
				avoidLabelOverlap: true,
				minAngle: 12,
				itemStyle: {
					borderRadius: 6,
					borderColor: '#fff',
					borderWidth: 2
				},
				label: {
					show: false,
					position: 'center'
				},
				emphasis: {
					label: {
						show: true,
						fontSize: 16,
						fontWeight: 'bold',
						formatter: '{b}'
					}
				},
				labelLine: {
					show: false
				},
				data: proyectosPorPrograma.map(p => ({
					name: p.programa || 'Sin Programa Asignado',
					value: parseInt(p.cantidad || 0)
				}))
			}
		]
	};

	// 📊 OPCIONES ECHARTS: Municipios Alcanzados (Gauge estilo Tablero V1)
	const optionGauge = {
		series: [{
			type: 'gauge',
			max: 135, // Total de municipios de PBA
			startAngle: 210,
			endAngle: -30,
			progress: {
				show: true,
				width: 14,
				itemStyle: { color: '#3b82f6' } // Color azul de la barra
			},
			axisLine: {
				lineStyle: { width: 14, color: [[1, '#e5e7eb']] } // Fondo gris claro
			},
			pointer: { show: false },
			axisTick: { show: false },
			splitLine: { show: false },
			axisLabel: {
				show: true,
				distance: -20,
				formatter: function (value) {
					if (value === 0 || value === 135) return value;
					return '';
				},
				color: '#9ca3af',
				fontSize: 12
			},
			detail: {
				valueAnimation: true,
				formatter: '{value}',
				offsetCenter: [0, '10%'],
				textStyle: {
					fontSize: 42,
					fontWeight: 'bold',
					color: '#2563eb' // 🔵 Azul institucional (blue-600)
				}
			},
			data: [{ value: metrics?.municipiosAlcanzados || 0 }]
		}]
	};

	return (
		<div className="p-6">
			<div className="flex items-center justify-between mb-2">
				<div className="flex items-center gap-3">
					<h1 className="text-2xl font-bold text-gray-800 dark:text-white">
						Tableros
					</h1>
				</div>
				<button
					type="button"
					onClick={generarPDF}
					disabled={loading || generandoPdf}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
						<path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
					</svg>
					{generandoPdf ? 'Generando PDF…' : 'Imprimir a PDF'}
				</button>
			</div>
			<p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
				Indicadores consolidados de las obras.
			</p>

			{error && (
				<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-md text-sm text-red-700 dark:text-red-300">
					Error al cargar los datos del tablero: {error.message || String(error)}
				</div>
			)}

			{/* Filtros de año por criterio múltiple */}
			<TablerosAdminFilters
				filters={filters}
				onFilterChange={(e) => updateFilters({ [e.target.name]: e.target.value })}
				onReset={resetFilters}
				loading={loading}
				isRefetching={isRefetching}
			/>

			{/* Fila Superior de KPIs (mismo formato que Tablero V1) */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-3 xl:grid-cols-3">
				<div className="rounded-xl border bg-white p-6 shadow-sm text-center flex flex-col justify-center dark:bg-gray-800 dark:border-gray-700">
					<h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">Total Proyectos</h3>
					<p className="mt-2 text-4xl font-bold text-blue-600 dark:text-blue-400">{loading ? '…' : (metrics?.totalObras ?? 0)}</p>
				</div>

				<div className="rounded-xl border bg-white p-6 shadow-sm text-center flex flex-col justify-center dark:bg-gray-800 dark:border-gray-700">
					<h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">Monto Conveniado</h3>
					<p className="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">
						{loading ? '…' : `$${((metrics?.presupuestoTotal ?? 0) / 1000000).toFixed(2)}M`}
					</p>
				</div>

				<div className="rounded-xl border bg-white p-6 shadow-sm flex flex-col items-center dark:bg-gray-800 dark:border-gray-700">
					<h3 className="text-sm font-medium text-gray-500 uppercase mb-2 dark:text-gray-400">Municipios Alcanzados</h3>
					<div className="h-40 w-40">
						<ReactECharts
							option={optionGauge}
							style={{ height: '100%', width: '100%' }}
							onChartReady={(instancia) => { gaugeRef.current = instancia; }}
						/>
					</div>
				</div>
			</div>

			{/* Gráfico de Proyectos por Partido */}
			<div className="mb-8 mt-8">
				<GraficoPartidos
					data={proyectosPorPartido}
					baseData={proyectosPorPartidoBase}
					partidoSeleccionados={filters.partido}
					loading={loading}
					mostrarTop15={mostrarTop15Partidos}
					onMostrarTop15Change={setMostrarTop15Partidos}
				/>
			</div>

			{/* Proyectos por Programa + Proyectos por Estado (Tablero V1) */}
			<div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
				<div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-[450px]">
					<h3 className="text-base font-bold text-gray-800 dark:text-white tracking-tight uppercase mb-4">Proyectos por Programa</h3>
					<div className="flex-1 w-full min-h-0">
						<ReactECharts
							option={optionTortaProgramas}
							style={{ height: '100%', width: '100%' }}
							notMerge={true}
							onChartReady={(instancia) => { tortaProgramasRef.current = instancia; }}
						/>
					</div>
				</div>
				<GraficoTortaEstados
					data={proyectosPorEstado}
					estadosFiltro={filters.estado}
					heightClass="h-[450px]"
					onChartReady={(instancia) => { tortaEstadosRef.current = instancia; }}
				/>
			</div>

			{/* Distribución Provincial de Obras + Región Metropolitana (RMBA) */}
			<div className="mb-8">
				<ComponenteMapas
					proyectosPorPartido={proyectosPorPartidoBase}
					partidoSeleccionadoId={filters.partido}
					onMapasListos={(registros) => { mapasRef.current = registros; }}
				/>
			</div>

			{/* % de Recursos Transferidos por Tipo de Subsidio por Año (desglosadas / apiladas) */}
			<div className="mb-8">
				<SelectorGraficoTipoSubsidio
					titulo="Porcentaje de recursos transferidos por Tipo de subsidio por Año"
					subtituloActivo="Distribución entre Municipios y ONGs/Universidades"
					GraficoDesglosado={GraficoRecursosPorTipoAnual}
					GraficoApilado={GraficoRecursosPorTipoAnualApilado}
					data={recursosPorSubsidioAnual}
					anioSeleccionado={filters.year}
					vistaInicial="apilado"
					onChartReady={(instancia) => { recursosAnualRef.current = instancia; }}
				/>
			</div>

			{/* Evolución del % de Proyectos por Tipo de subsidio por Año (desglosadas / apiladas) */}
			<div className="mb-8">
				<SelectorGraficoTipoSubsidio
					titulo="Evolución del Porcentaje de los Proyectos por Tipo de subsidio por Año"
					subtituloActivo="Distribución entre Municipios y ONGs/Universidades"
					GraficoDesglosado={GraficoSubsidiosAnual}
					GraficoApilado={GraficoSubsidiosAnualApilado}
					data={proyectosPorSubsidioAnual}
					anioSeleccionado={filters.year}
					vistaInicial="desglosado"
					onChartReady={(instancia) => { proyectosAnualRef.current = instancia; }}
				/>
			</div>
		</div>
	);
};
